// ════════════════════════════════════════════════════════════
//  IslandClouds.jsx — THE CLOUDS THAT HOLD THE STANDS UP
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "if you want it to look like its holding it let's
//  just really go for it >:3 for this one, I want clouds with movement
//  holding them up"
//
//  Under every floating island a cloud bank swells round its rock: big
//  puffs up top pressed against the slab (doing the holding), smaller
//  ones tapering down the keel, a few wisps trailing below. Every puff
//  wanders round its own spot, rolls and breathes, and rides its island's
//  bob (islandBob — the same number the rock and the crowd use).
//
//  ONE InstancedMesh, ONE draw call, all motion in the vertex shader — a
//  per-frame cost of a few uniforms however many puffs there are.
//  Lit by hand (no scene lights touched): a pale lit top, a shaded belly,
//  a breath of Khao underneath — the Unstable World's clouds, not Earth's.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { canvasTex, mulberry32 } from "../ieBeingModels.jsx";
import { ISLANDS, islandBob } from "./StadiumCrowd.jsx";
import { CLOUDS } from "../data/ringside.js";

// a soft, lumpy puff: several overlapping blobs, so no two edges are perfect circles
function puffTex() {
  return canvasTex("ringside_cloud_puff_v2", 256, 256, (g, w) => {
    const R = mulberry32(31337);
    g.clearRect(0, 0, w, w);
    for (let i = 0; i < 18; i++) {
      const a = R() * Math.PI * 2, d = R() * w * 0.22;
      const x = w / 2 + Math.cos(a) * d, y = w / 2 + Math.sin(a) * d * 0.75, r = w * (0.2 + R() * 0.2);
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, "rgba(255,255,255,0.7)");
      grd.addColorStop(0.6, "rgba(255,255,255,0.35)");
      grd.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grd; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }
  });
}

// every puff, placed in its island's own frame (along the arc, out from the ring, down the keel)
function puffPlan() {
  const R = mulberry32(5150), C = CLOUDS, out = [];
  ISLANDS.forEach((isle, ii) => {
    const d = isle.deck, depth = d.rows * d.dr + 0.5, mid = d.r0 + depth / 2;
    const ang = isle.phi0 + isle.len / 2, width = isle.len * mid;
    const tan = [Math.cos(ang), -Math.sin(ang)], rad = [Math.sin(ang), Math.cos(ang)];
    const base = d.y0 - 1.3;   // the slab's underside
    for (let k = 0; k < C.perIsland; k++) {
      // most puffs pack up against the slab; fewer, smaller ones the further down the keel
      const down = Math.pow(R(), 1.6);                         // 0 = against the slab, 1 = the keel's tip
      const spread = 1 - down * 0.7;                           // the bank narrows as it goes down
      const u = (R() - 0.5) * width * 1.1 * spread, v = (R() - 0.5) * depth * 1.5 * spread;
      const size = (C.size[0] + R() * (C.size[1] - C.size[0])) * (1 - down * 0.5);
      // sunk by its own size: only the top of a puff kisses the slab's underside,
      // so the bank lifts the island without ever rising in front of the crowd
      const y = base - size * 0.3 - down * d.keel * 0.85;
      out.push({
        x: Math.sin(ang) * mid + tan[0] * u + rad[0] * v,
        z: Math.cos(ang) * mid + tan[1] * u + rad[1] * v,
        y, size, isle: ii,
        ph: R() * Math.PI * 2, sp: C.speed[0] + R() * (C.speed[1] - C.speed[0]),
        shade: 0.85 + R() * 0.15, down,
      });
    }
    // a couple of wisps trailing off below, drifting slower and further
    for (let k = 0; k < 2; k++) {
      out.push({
        x: Math.sin(ang) * mid + tan[0] * (R() - 0.5) * width * 0.6, z: Math.cos(ang) * mid + tan[1] * (R() - 0.5) * width * 0.6,
        y: base - d.keel * (1.05 + R() * 0.25), size: C.size[0] * (0.5 + R() * 0.4), isle: ii,
        ph: R() * Math.PI * 2, sp: C.speed[0] * 0.6, shade: 0.75 + R() * 0.15, down: 1,
      });
    }
  });
  return out;
}

const VERT = /* glsl */`
  attribute vec4 aPuff;     // x = phase, y = speed, z = size, w = shade
  attribute vec2 aBank;     // x = which island, y = how far down the bank (0 = against the slab, 1 = the bottom)
  uniform float uTime, uDrift;
  uniform float uIsle[${ISLANDS.length}];
  varying vec2 vUv;
  varying float vShade, vY, vDown;
  void main() {
    vec3 c = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float t = uTime * aPuff.y + aPuff.x;
    // wander round its spot, roll up and down, breathe
    c += vec3(sin(t), sin(t * 0.73 + 1.3) * 0.45, cos(t * 0.91)) * uDrift;
    c.y += uIsle[int(aBank.x + 0.5)];
    float breathe = 1.0 + sin(uTime * 0.35 + aPuff.x * 3.0) * 0.08;
    float s = aPuff.z * breathe;
    // face the camera fully, and spin slowly in its own plane so the lumps roll
    float rot = aPuff.x + uTime * aPuff.y * 0.6;
    vec2 p = vec2(position.x * cos(rot) - position.y * sin(rot), position.x * sin(rot) + position.y * cos(rot));
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up    = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
    vec3 w = c + (right * p.x + up * p.y) * s;
    vUv = uv; vShade = aPuff.w; vY = position.y; vDown = aBank.y;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const FRAG = /* glsl */`
  uniform sampler2D uMap;
  uniform vec3 uTop, uBelly, uGlow;
  varying vec2 vUv;
  varying float vShade, vY, vDown;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    if (t.a < 0.01) discard;
    // shaded by where the puff sits in the WHOLE bank (lit against the slab, dark
    // toward the bottom), with only a hint of its own roundness — one mass, not balls
    float lit = clamp((1.0 - vDown) * 0.85 + smoothstep(-0.5, 0.5, vY) * 0.15, 0.0, 1.0);
    vec3 col = mix(uBelly, uTop, lit) * vShade;
    col += uGlow * (1.0 - lit) * 0.22;                      // a breath of Khao in the belly
    gl_FragColor = vec4(col, t.a * 0.82);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export default function IslandClouds() {
  const puffs = useMemo(() => puffPlan(), []);
  const tex = useMemo(() => puffTex(), []);
  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(1, 1);
    const pa = new Float32Array(puffs.length * 4), ba = new Float32Array(puffs.length * 2);
    puffs.forEach((p, i) => { pa.set([p.ph, p.sp, p.size, p.shade], i * 4); ba.set([p.isle, p.down], i * 2); });
    g.setAttribute("aPuff", new THREE.InstancedBufferAttribute(pa, 4));
    g.setAttribute("aBank", new THREE.InstancedBufferAttribute(ba, 2));
    return g;
  }, [puffs]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false,
    uniforms: {
      uMap: { value: tex }, uTime: { value: 0 }, uDrift: { value: CLOUDS.drift },
      uIsle: { value: new Float32Array(ISLANDS.length) },
      uTop: { value: new THREE.Color(CLOUDS.top) }, uBelly: { value: new THREE.Color(CLOUDS.belly) }, uGlow: { value: new THREE.Color(CLOUDS.glow) },
    },
  }), [tex]);
  const mesh = useMemo(() => {
    const m = new THREE.InstancedMesh(geo, mat, puffs.length);
    const o = new THREE.Object3D();
    puffs.forEach((p, i) => { o.position.set(p.x, p.y, p.z); o.updateMatrix(); m.setMatrixAt(i, o.matrix); });
    m.instanceMatrix.needsUpdate = true;
    m.frustumCulled = false;
    m.renderOrder = 2;   // after the opaque world, so the clouds sit over the rock and the void
    return m;
  }, [geo, mat, puffs]);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    mat.uniforms.uTime.value = t;
    const isle = mat.uniforms.uIsle.value;
    for (let i = 0; i < isle.length; i++) isle[i] = islandBob(i, t);
  });
  return <primitive object={mesh} />;
}

export const CLOUD_COUNT = () => puffPlan().length;
